import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, apiWrite } from '../lib/api'
import { Empty, PageHead, Skel } from '../components/ui'
import { thaiDate, thaiDateTime } from '../lib/format'

// Contract expected from the renewal handler (part 143):
//   GET   /v1/renewals  -> RenewalSnapshot
//   PATCH /v1/renewals  { auto_renew: boolean } -> RenewalSnapshot
// The page is deliberately self-contained: types and the query live here so the
// new screen does not widen the shared api/queries surface.

type SubStatus = 'trialing' | 'active' | 'past_due' | 'canceled'
type NoticeKind = 't_minus_30' | 't_minus_7' | 't_minus_0' | 'past_due' | 'canceled'
type DunningState = '' | 'reminder' | 'restricted' | 'cancel'

interface Subscription {
  id: string
  plan_key: string
  status: SubStatus
  period_start: string
  period_end: string
  seats: number
  auto_renew: boolean
}

interface RenewalEvent {
  id: number
  kind: NoticeKind
  channel: string
  sent_at: string
}

interface RenewalSnapshot {
  subscription: Subscription
  plan?: { name: string; price_satang: number } | null
  dunning_days?: number
  dunning_state?: DunningState
  events: RenewalEvent[]
}

const statusLabel: Record<SubStatus, string> = {
  trialing: 'ทดลองใช้',
  active: 'ใช้งาน',
  past_due: 'ค้างชำระ',
  canceled: 'ยกเลิก',
}

const planLabel: Record<string, string> = {
  starter: 'Starter',
  growth: 'Growth',
  enterprise: 'Enterprise',
}

const kindLabel: Record<NoticeKind, string> = {
  t_minus_30: 'แจ้งเตือนล่วงหน้า 30 วัน',
  t_minus_7: 'แจ้งเตือนล่วงหน้า 7 วัน',
  t_minus_0: 'ถึงวันต่ออายุ',
  past_due: 'เริ่มค้างชำระ',
  canceled: 'ยกเลิกสมาชิก',
}

const kindTone: Record<NoticeKind, string> = {
  t_minus_30: 'var(--color-ivory-dim)',
  t_minus_7: 'var(--color-amber)',
  t_minus_0: 'var(--color-gold)',
  past_due: 'var(--color-amber)',
  canceled: 'var(--color-critical)',
}

const dunningLabel: Record<Exclude<DunningState, ''>, string> = {
  reminder: 'เตือนชำระ',
  restricted: 'จำกัดการใช้งาน',
  cancel: 'ระงับและยกเลิก',
}

const dunningHint: Record<Exclude<DunningState, ''>, string> = {
  reminder: 'เลยกำหนดชำระแล้ว 1–6 วัน ระบบยังใช้งานได้เต็มรูปแบบ',
  restricted: 'เลยกำหนดชำระ 7–13 วัน การใช้งานบางส่วนถูกจำกัด',
  cancel: 'เลยกำหนดชำระ 14 วันขึ้นไป เตรียมระงับสมาชิก',
}

function utcDay(iso: string): number {
  const [y, m, d] = iso.slice(0, 10).split('-').map(Number)
  return Date.UTC(y, m - 1, d)
}

function daysUntil(iso: string): number {
  const now = new Date()
  const today = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate())
  return Math.round((utcDay(iso) - today) / 86_400_000)
}

function dunningFor(daysPastDue: number): DunningState {
  if (daysPastDue <= 0) return ''
  if (daysPastDue < 7) return 'reminder'
  if (daysPastDue < 14) return 'restricted'
  return 'cancel'
}

export function Renewals() {
  const qc = useQueryClient()
  const q = useQuery({ queryKey: ['renewals'], queryFn: () => api<RenewalSnapshot>('/v1/renewals'), staleTime: 20_000 })
  const [autoRenew, setAutoRenew] = useState<boolean | null>(null)

  const toggle = useMutation({
    mutationFn: (next: boolean) => apiWrite<RenewalSnapshot>('/v1/renewals', 'PATCH', { auto_renew: next }),
    onSuccess: (res) => {
      setAutoRenew(res.subscription.auto_renew)
      qc.setQueryData(['renewals'], res)
    },
    onError: () => setAutoRenew(null),
  })

  if (q.isLoading) {
    return (
      <>
        <PageHead title="การต่ออายุสมาชิก" sub="แจ้งเตือนก่อนรอบต่ออายุและติดตามยอดค้างชำระ" />
        <div className="stack"><Skel h={190} /><Skel h={140} /></div>
      </>
    )
  }

  const snap = q.data
  if (q.isError || !snap) {
    return (
      <>
        <PageHead title="การต่ออายุสมาชิก" sub="แจ้งเตือนก่อนรอบต่ออายุและติดตามยอดค้างชำระ" />
        <Empty title="โหลดข้อมูลการต่ออายุไม่ได้" body="ลองรีเฟรชหน้าอีกครั้ง หรือตรวจสอบการเชื่อมต่อกับเซิร์ฟเวอร์" />
      </>
    )
  }

  const sub = snap.subscription
  const left = daysUntil(sub.period_end)
  const daysPastDue = Math.max(0, -left)
  const dunning: DunningState = snap.dunning_state !== undefined
    ? snap.dunning_state
    : sub.status === 'past_due' ? dunningFor(daysPastDue) : ''
  const dunningDays = snap.dunning_days ?? daysPastDue
  const events = snap.events ?? []
  const planName = snap.plan?.name ?? planLabel[sub.plan_key] ?? sub.plan_key
  const isOn = autoRenew ?? sub.auto_renew
  const busy = toggle.isPending

  return (
    <>
      <PageHead title="การต่ออายุสมาชิก" sub="แจ้งเตือนล่วงหน้า 30 วัน 7 วัน และวันต่ออายุ · เก็บประวัติทุกครั้งที่แจ้ง" />

      <div className="grid-main">
        <div className="card card-pad">
          <div className="card-label">แพ็กเกจปัจจุบัน</div>
          <div className="row" style={{ marginTop: 12, alignItems: 'baseline', gap: 12 }}>
            <span className="display-m">{planName}</span>
            <span className="chip gold">{statusLabel[sub.status] ?? sub.status}</span>
            {dunning ? (
              <span className="chip" style={{ color: 'var(--color-amber)', borderColor: 'rgba(199, 123, 43, 0.35)' }}>
                {dunningLabel[dunning]}
              </span>
            ) : null}
          </div>

          <div className="row" style={{ marginTop: 24, gap: 34, flexWrap: 'wrap' }}>
            <div>
              <div className="eyebrow">รอบปัจจุบัน</div>
              <div style={{ marginTop: 6, fontSize: 13 }}>
                {thaiDate(sub.period_start)} — {thaiDate(sub.period_end)}
              </div>
            </div>
            <div>
              <div className="eyebrow">ที่นั่ง</div>
              <div className="heading-l tnum" style={{ marginTop: 6 }}>{sub.seats}</div>
            </div>
            <div>
              <div className="eyebrow">เหลืออีก</div>
              <div className="heading-l tnum" style={{ marginTop: 6 }}>
                {left >= 0 ? left : 0}<span className="stat-unit">วัน</span>
              </div>
            </div>
            {snap.plan ? (
              <div>
                <div className="eyebrow">ค่าบริการต่อรอบ</div>
                <div className="heading-l tnum" style={{ marginTop: 6 }}>
                  ฿{(snap.plan.price_satang / 100).toLocaleString('th-TH', { minimumFractionDigits: 2 })}
                </div>
              </div>
            ) : null}
          </div>

          <div className="rule" style={{ margin: '22px 0 16px' }} />

          <div className="row">
            <div>
              <div style={{ fontSize: 12.5 }}>ต่ออายุอัตโนมัติ</div>
              <div className="dim" style={{ fontSize: 11.5, marginTop: 4 }}>
                เปิดไว้จะออกใบแจ้งหนี้และเริ่มรอบถัดไปให้อัตโนมัติ · ปิดได้ทุกเมื่อก่อนวันต่ออายุ
              </div>
            </div>
            <label className="row" style={{ gap: 10, marginLeft: 'auto', cursor: busy ? 'wait' : 'pointer' }}>
              <span style={{ fontSize: 11.5, color: 'var(--color-ivory-dim)' }}>{isOn ? 'เปิด' : 'ปิด'}</span>
              <span style={{ position: 'relative', display: 'inline-flex', width: 40, height: 22, flex: 'none' }}>
                <input
                  type="checkbox"
                  role="switch"
                  aria-label="ต่ออายุอัตโนมัติ"
                  checked={isOn}
                  disabled={busy}
                  onChange={(e) => {
                    const next = e.target.checked
                    setAutoRenew(next)
                    toggle.mutate(next)
                  }}
                  style={{
                    position: 'absolute', inset: 0, margin: 0, appearance: 'none', WebkitAppearance: 'none',
                    background: isOn ? 'var(--color-gold)' : 'var(--color-hairline-strong)',
                    border: `1px solid ${isOn ? 'var(--color-gold)' : 'var(--color-hairline-strong)'}`,
                    borderRadius: 'var(--radius-full)',
                    cursor: busy ? 'wait' : 'pointer',
                    opacity: busy ? 0.6 : 1,
                    transition: 'background var(--micro) var(--ease-standard)',
                  }}
                />
                <span
                  aria-hidden="true"
                  style={{
                    position: 'absolute', top: 2, left: isOn ? 20 : 2, width: 18, height: 18,
                    borderRadius: '50%', pointerEvents: 'none',
                    background: isOn ? '#1A1712' : 'var(--color-ivory-dim)',
                    transition: 'left var(--micro) var(--ease-standard)',
                  }}
                />
              </span>
            </label>
          </div>
          {toggle.isError ? (
            <div className="dim" style={{ fontSize: 11.5, marginTop: 10, color: 'var(--color-amber)' }}>
              บันทึกการตั้งค่าไม่สำเร็จ — ค่าเดิมยังมีผลอยู่
            </div>
          ) : null}
        </div>

        <div className="card card-pad">
          <div className="card-label">สถานะการเรียกเก็บ</div>
          {dunning ? (
            <>
              <div className="row" style={{ marginTop: 14, alignItems: 'baseline', gap: 10 }}>
                <span className="display-m" style={{ color: 'var(--color-amber)' }}>{dunningLabel[dunning]}</span>
                <span className="dim tnum" style={{ fontSize: 12 }}>{dunningDays} วัน</span>
              </div>
              <p className="dim" style={{ fontSize: 12.5, lineHeight: 1.7, marginTop: 10 }}>
                {dunningHint[dunning]}
              </p>
              <div className="rule" style={{ margin: '16px 0' }} />
              <dl className="kv" style={{ gridTemplateColumns: '1fr auto' }}>
                <dt>ครบกำหนดรอบนี้</dt><dd style={{ textAlign: 'right' }}>{thaiDate(sub.period_end)}</dd>
                <dt>เลยกำหนดมาแล้ว</dt><dd className="tnum" style={{ textAlign: 'right' }}>{dunningDays} วัน</dd>
                <dt>สถานะสมาชิก</dt><dd style={{ textAlign: 'right' }}>{statusLabel[sub.status] ?? sub.status}</dd>
              </dl>
            </>
          ) : (
            <>
              <div className="row" style={{ marginTop: 14, alignItems: 'baseline', gap: 10 }}>
                <span className="display-m" style={{ color: 'var(--color-emerald-soft)' }}>ปกติ</span>
                <span className="chip">ไม่มียอดค้าง</span>
              </div>
              <p className="dim" style={{ fontSize: 12.5, lineHeight: 1.7, marginTop: 10 }}>
                สมาชิกอยู่ในรอบที่ชำระแล้ว ระบบจะแจ้งเตือนอีกครั้งเมื่อเข้าสู่ช่วง 30 วันก่อนวันต่ออายุ
              </p>
              <div className="rule" style={{ margin: '16px 0' }} />
              <dl className="kv" style={{ gridTemplateColumns: '1fr auto' }}>
                <dt>รอบถัดไป</dt><dd style={{ textAlign: 'right' }}>{thaiDate(sub.period_end)}</dd>
                <dt>ต่ออายุอัตโนมัติ</dt><dd style={{ textAlign: 'right' }}>{isOn ? 'เปิด' : 'ปิด'}</dd>
                <dt>สถานะสมาชิก</dt><dd style={{ textAlign: 'right' }}>{statusLabel[sub.status] ?? sub.status}</dd>
              </dl>
            </>
          )}
        </div>
      </div>

      <div style={{ marginTop: 24 }}>
        <div className="card-label" style={{ marginBottom: 10 }}>ไทม์ไลน์การแจ้งเตือน</div>
        <div className="card card-pad">
          {events.length === 0 ? (
            <div className="dim" style={{ fontSize: 12.5 }}>
              ยังไม่มีการแจ้งเตือน — ระบบจะเริ่มบันทึกเมื่อเข้าสู่ช่วง 30 วันก่อนรอบต่ออายุ
            </div>
          ) : (
            events.map((e, i) => (
              <div
                key={e.id}
                className="row"
                style={{
                  alignItems: 'flex-start', gap: 12, padding: '12px 0',
                  borderBottom: i === events.length - 1 ? 'none' : '1px solid var(--color-hairline)',
                }}
              >
                <span
                  aria-hidden="true"
                  style={{
                    width: 8, height: 8, borderRadius: '50%', marginTop: 5, flex: 'none',
                    background: kindTone[e.kind] ?? 'var(--color-ivory-dim)',
                  }}
                />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 12.5 }}>{kindLabel[e.kind] ?? e.kind}</div>
                  <div className="dim" style={{ fontSize: 11, marginTop: 3 }}>
                    {e.channel === 'failed' ? 'ส่งไม่สำเร็จ' : 'ส่งผ่านช่องทางที่สมัครไว้'}
                  </div>
                </div>
                <div className="mono dim" style={{ fontSize: 11, whiteSpace: 'nowrap' }}>{thaiDateTime(e.sent_at)}</div>
              </div>
            ))
          )}
        </div>
      </div>
    </>
  )
}
